/**
 * region.js — Round 40i — single source of truth for the user's
 * payment region on the frontend.
 *
 * Resolves the user's country + currency + provider via
 * GET /api/region/me (which honours ?force_region=US for testing).
 * Caches the result on `window` so multiple components don't refetch.
 *
 * Round 40n — cache is now keyed on the current auth token. A
 * pre-login anon visit (where IP detection drives the region) used
 * to lock the user into INR for the rest of the tab session. Now,
 * when the token changes (login / logout / verify), the cache key
 * changes too, so the very next fetchRegion() call hits the server
 * with the new auth context and gets the persisted residence_country.
 *
 * Usage:
 *   const { country, currency, provider } = await fetchRegion();
 *   if (currency === 'USD') { ...PayPal branch... }
 *   else { ...Razorpay branch... }
 */
import { api } from '@/lib/api';

const CACHE_KEY_PREFIX = '__astroconsult_region__';

function _currentCacheKey() {
    // Different cache slot per auth identity. `anon` for logged-out
    // visitors; the token suffix isolates each logged-in session so
    // a user-switch on the same browser doesn't inherit stale data.
    const tok = (typeof window !== 'undefined' && window.localStorage)
        ? (localStorage.getItem('ac_token') || '')
        : '';
    return tok ? `${CACHE_KEY_PREFIX}__${tok.slice(-24)}` : `${CACHE_KEY_PREFIX}__anon`;
}

export async function fetchRegion({ force = false } = {}) {
    const key = _currentCacheKey();
    if (!force && typeof window !== 'undefined' && window[key]) {
        return window[key];
    }
    try {
        // Preserve `?force_region=` from the URL so deep-links pick up
        // the same override the backend honours.
        const forceParam = new URLSearchParams(window.location.search).get('force_region');
        const url = forceParam ? `/region/me?force_region=${encodeURIComponent(forceParam)}` : '/region/me';
        const { data } = await api.get(url);
        const region = {
            country: data.country || 'IN',
            currency: data.currency || 'INR',
            provider: data.provider || 'razorpay',
            persisted: !!data.persisted,
            symbol: data.currency === 'USD' ? '$' : '₹',
        };
        if (typeof window !== 'undefined') window[key] = region;
        return region;
    } catch {
        // Conservative fallback to INR so India users never lose
        // their existing flow on any transient blip.
        const region = { country: 'IN', currency: 'INR', provider: 'razorpay', persisted: false, symbol: '₹' };
        if (typeof window !== 'undefined') window[key] = region;
        return region;
    }
}

export function clearRegionCache() {
    if (typeof window === 'undefined') return;
    // Wipe every region slot — covers the anon slot AND any prior
    // logged-in slot still sitting on `window`.
    Object.keys(window)
        .filter((k) => k.startsWith(CACHE_KEY_PREFIX))
        .forEach((k) => { delete window[k]; });
}

export function formatPrice(amount, currency) {
    if (currency === 'USD') return `$${Number(amount).toLocaleString('en-US', { maximumFractionDigits: 2 })}`;
    return `₹${Number(amount).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
}

/**
 * Load the PayPal JS SDK on-demand. Returns a promise that resolves
 * to `window.paypal` once ready. Idempotent — caches the loader
 * promise so we never inject duplicate <script> tags.
 */
let _paypalSdkPromise = null;
export function loadPaypalSdk({ clientId, currency = 'USD' }) {
    if (typeof window === 'undefined') return Promise.resolve(null);
    if (window.paypal) return Promise.resolve(window.paypal);
    if (_paypalSdkPromise) return _paypalSdkPromise;
    if (!clientId) return Promise.reject(new Error('PayPal client_id missing'));
    _paypalSdkPromise = new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = `https://www.paypal.com/sdk/js?client-id=${encodeURIComponent(clientId)}&currency=${encodeURIComponent(currency)}&intent=capture&disable-funding=credit,card`;
        script.async = true;
        script.onload = () => resolve(window.paypal);
        script.onerror = (e) => { _paypalSdkPromise = null; reject(e); };
        document.head.appendChild(script);
    });
    return _paypalSdkPromise;
}
